import { csvCell } from '@nest-recruit/shared';
import { EMPTY } from '../../lib/format';
import type { Column } from './types';

// S32 (DECISIONS-LOG S29–S32 Q27): "Export selected" and "Copy links" on every record table.
// The rows are what the server already sent — masked fields arrive masked (#5) — so an export
// can never show more than the screen does.

/** Lets Excel open the file as UTF-8, so a Bangla name survives. */
const BYTE_ORDER_MARK = '﻿';

export function exportValueOf<TRow>(column: Column<TRow>, row: TRow): string {
  if (column.exportValue) return textOf(column.exportValue(row));
  const shown = column.cell(row);
  return typeof shown === 'string' || typeof shown === 'number' ? String(shown) : '';
}

export function toCsv<TRow>(columns: readonly Column<TRow>[], rows: readonly TRow[]): string {
  // The app's one formula guard (review W1), shared with every server export.
  const lines = [columns.map((column) => csvCell(column.header)), ...rows.map((row) => columns.map((column) => csvCell(exportValueOf(column, row))))];
  return BYTE_ORDER_MARK + lines.map((cells) => cells.join(',')).join('\r\n');
}

/**
 * "Copy row" (DECISIONS-LOG 2026-10-06): one "Heading: value" per line, for pasting into a
 * message. An empty or restricted cell copies as the dash the screen shows; a cell drawn as
 * something richer than text, with no `exportValue`, is left out as it is from the CSV.
 */
export function toLabelledText<TRow>(columns: readonly Column<TRow>[], row: TRow): string {
  return columns
    .flatMap((column) => {
      const shown = column.cell(row);
      if (shown === null || shown === undefined || shown === '') return [`${column.header}: ${EMPTY}`];
      const value = exportValueOf(column, row);
      return value === '' ? [] : [`${column.header}: ${value}`];
    })
    .join('\n');
}

export function downloadCsv(fileName: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/** `requisitions-2026-10-02.csv`, dated where the person is. */
export function exportFileName(name: string, now: Date = new Date()): string {
  return `${name}-${new Intl.DateTimeFormat('en-CA').format(now)}.csv`;
}

function textOf(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}
